import React from "react";
import { useState } from "react";
import { useEffect } from "react";
import { getAllSellersApiCaller } from "shared/endPoints/apiCallers";
import HTable from "material-htable2";
import { intlTranselate } from "shared/utils/ppUtils";
import { Box } from "@mui/system";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
  Fab,
  Zoom,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio
} from "@mui/material";
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ApiRequest from "../../../actions/ApiRequest";

const propTypes = {};
const defaultProps = {};

export default function Dealers(props) {
  const [allSellers, setAllSellers] = useState([]);
  const [isLoading, setLoading] = useState(false);

  // Unified Notification State
  const [showUnifiedNotificationModal, setShowUnifiedNotificationModal] = useState(false);
  const [selected, setSelected] = useState([]);
  const [notificationLoading, setNotificationLoading] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState('all');

  const [showScrollButtons, setShowScrollButtons] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const [fromSellerId, setFromSellerId] = useState("");
  const [toSellerId, setToSellerId] = useState("");
  const [tableKey, setTableKey] = useState(0);

  async function getSellers() {
    setLoading(true);
    try {
      const {
        data: { all_sellers },
      } = await getAllSellersApiCaller({ resellerId: "" });
      setAllSellers(all_sellers || []);
    } catch (error) { }
    setLoading(false);
  }

  useEffect(() => {
    getSellers();
  }, []);

  // Handle scroll visibility for floating buttons
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      setShowScrollButtons(scrollTop > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Smooth scroll to top
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  // Smooth scroll to bottom
  const scrollToBottom = () => {
    window.scrollTo({
      top: document.documentElement.scrollHeight,
      behavior: 'smooth'
    });
  };

  function handleSelectRange() {
    if (!fromSellerId || !toSellerId) return;

    const start = parseInt(fromSellerId, 10);
    const end = parseInt(toSellerId, 10);

    if (isNaN(start) || isNaN(end)) return;

    const newSelected = allSellers
      .filter((seller) => {
        const sellerId = parseInt(seller.seller_id, 10);
        return !isNaN(sellerId) && sellerId >= start && sellerId <= end;
      })
      .map((seller) => seller.seller_id);

    console.log("Range Selection:", { start, end, count: newSelected.length, newSelected });
    setSelected(newSelected);
    setTableKey((prev) => prev + 1);
  }

  // Filter sellers based on search query
  const filteredSellers = allSellers.filter((seller) => {
    if (!searchQuery.trim()) return true;

    const query = searchQuery.toLowerCase().trim();
    const sellerId = (seller.seller_id || "").toString().toLowerCase();
    const name = (seller.name || "").toLowerCase();
    const mobile = (seller.mobile_number || "").toLowerCase();

    return (
      sellerId.includes(query) ||
      name.includes(query) ||
      mobile.includes(query)
    );
  });

  function tableData() {
    const returnVal = { heads: [], rows: [] };

    returnVal.heads = [
      {
        component: intlTranselate("seller_id"),
      },
      {
        component: intlTranselate("seller_name"),
      },
      {
        component: intlTranselate("Mobile number"),
      },
    ];

    filteredSellers.forEach((seller) => {
      const isSelected = selected.includes(seller.seller_id);
      const cellStyle = isSelected ? { color: '#1976d2', fontWeight: 'bold' } : {};

      const cells = [
        {
          component: <Typography sx={cellStyle}>{seller.seller_id}</Typography>,
        },
        {
          component: <Typography sx={cellStyle}>{seller.name}</Typography>,
        },
        {
          component: <Typography sx={cellStyle}>{seller.mobile_number}</Typography>,
        },
      ];
      returnVal.rows.push({
        id: seller.seller_id,
        isRowSelected: isSelected,
        cells,
      });
    });

    return returnVal;
  }

  async function onSendUnifiedNotification(e) {
    e.preventDefault();
    setNotificationLoading(true);

    const title = e.target.title?.value || '';
    const message = e.target.message?.value;

    try {
      const response = await ApiRequest.post('send_notification_bulk', {
        seller_ids: selected,
        channel: selectedChannel,
        title: title,
        message: message
      });

      const data = response.data;

      if (data.status === 'success') {
        const results = data.results;
        let resultMessage = `Notifications sent via ${data.channels.join(', ').toUpperCase()}!\n\n`;

        // Show results for each channel
        data.channels.forEach(channel => {
          const channelData = results[channel];
          resultMessage += `${channel.toUpperCase()}:\n`;
          resultMessage += `  ✓ Success: ${channelData.success}\n`;
          resultMessage += `  ✗ Failed: ${channelData.failed}\n`;

          // Show failed details for this channel
          const failed = channelData.details.filter(d => d.status === 'failed');
          if (failed.length > 0) {
            resultMessage += `  Failed sellers:\n`;
            failed.forEach(seller => {
              resultMessage += `    • ${seller.name} (${seller.seller_id}): ${seller.reason}\n`;
            });
          }
          resultMessage += '\n';
        });

        alert(resultMessage);
      } else {
        alert(`Failed to send notifications: ${data.reason || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Failed to send unified notification:', error);
      const reason = error?.response?.data?.reason || error.message || 'Unknown error';
      alert(`Error sending notifications: ${reason}`);
    }

    setNotificationLoading(false);
    setShowUnifiedNotificationModal(false);
  }

  function renderUnifiedNotificationDialog() {
    return (
      <Dialog
        fullWidth
        open={showUnifiedNotificationModal}
        onClose={() => !notificationLoading && setShowUnifiedNotificationModal(false)}
      >
        <DialogTitle>Send Notification</DialogTitle>
        <form onSubmit={onSendUnifiedNotification}>
          <DialogContent>
            <Stack spacing={2}>
              <FormControl>
                <FormLabel id="channel-group-label">Notification Channel</FormLabel>
                <RadioGroup
                  row
                  aria-labelledby="channel-group-label"
                  name="channel"
                  value={selectedChannel}
                  onChange={(e) => setSelectedChannel(e.target.value)}
                >
                  <FormControlLabel value="all" control={<Radio />} label="ALL" />
                  <FormControlLabel value="sms" control={<Radio />} label="SMS" />
                  <FormControlLabel value="whatsapp" control={<Radio />} label="WhatsApp" />
                  <FormControlLabel value="web" control={<Radio />} label="Web Notification" />
                </RadioGroup>
              </FormControl>

              {(selectedChannel === 'web' || selectedChannel === 'all') && (
                <TextField
                  autoFocus
                  margin="dense"
                  label="Notification Title"
                  type="text"
                  name="title"
                  fullWidth
                  variant="outlined"
                  required={selectedChannel === 'web'}
                  disabled={notificationLoading}
                />
              )}

              <TextField
                multiline
                rows={4}
                margin="dense"
                label="Message"
                type="text"
                name="message"
                fullWidth
                variant="outlined"
                required
                disabled={notificationLoading}
              />

              <Typography variant="caption" color="text.secondary">
                Sending to {selected.length} selected seller(s) via {selectedChannel.toUpperCase()}
              </Typography>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setShowUnifiedNotificationModal(false)}
              disabled={notificationLoading}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              type="submit"
              disabled={notificationLoading}
            >
              {notificationLoading ? 'Sending...' : 'Send'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    );
  }

  const { heads, rows } = tableData();
  return (
    <React.Fragment>
      {renderUnifiedNotificationDialog()}
      <Box paddingY={2}>
        {/* Search and Action Buttons */}
        <Stack direction={"row"} gap={2} paddingBottom={2} flexWrap="wrap">
          <TextField
            placeholder="Search by Name, ID, or Mobile..."
            variant="outlined"
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{
              minWidth: 300,
              flexGrow: 1,
              maxWidth: 500,
            }}
            InputProps={{
              startAdornment: (
                <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                  🔍
                </Box>
              ),
            }}
          />

          <Button
            variant="contained"
            color="primary"
            onClick={() => setShowUnifiedNotificationModal(true)}
            disabled={selected.length === 0}
          >
            Send Notification
          </Button>
          <TextField
            label="From Seller ID"
            variant="outlined"
            size="small"
            value={fromSellerId}
            onChange={(e) => setFromSellerId(e.target.value)}
          />
          <TextField
            label="To Seller ID"
            variant="outlined"
            size="small"
            value={toSellerId}
            onChange={(e) => setToSellerId(e.target.value)}
          />
          <Button variant="outlined" onClick={handleSelectRange}>
            Select Range
          </Button>
        </Stack>

        {/* Results Count */}
        {searchQuery && (
          <Box paddingBottom={1}>
            <Typography variant="body2" color="text.secondary">
              Found {filteredSellers.length} dealer{filteredSellers.length !== 1 ? 's' : ''}
              {allSellers.length > 0 && ` out of ${allSellers.length} total`}
            </Typography>
          </Box>
        )}

        <HTable
          key={tableKey}
          selectable
          selectOptoins={{
            defaultSelectedIds: selected,
          }}
          onSelectAllClick={(_, x, selectedIds) => {
            setSelected(selectedIds);
          }}
          onRowSelected={(_, selectedIds) => {
            setSelected(selectedIds);
          }}
          isLoading={isLoading}
          rows={rows}
          heads={heads}
        />
      </Box>

      {/* Floating Scroll Buttons */}
      <Zoom in={showScrollButtons}>
        <Box
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            zIndex: 1000,
          }}
        >
          {/* Scroll to Top Button */}
          <Fab
            color="primary"
            size="medium"
            onClick={scrollToTop}
            sx={{
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              '&:hover': {
                transform: 'scale(1.1)',
                boxShadow: '0 6px 16px rgba(0, 0, 0, 0.2)',
              },
              transition: 'all 0.3s ease',
            }}
            aria-label="scroll to top"
          >
            <KeyboardArrowUpIcon />
          </Fab>

          {/* Scroll to Bottom Button */}
          <Fab
            color="primary"
            size="medium"
            onClick={scrollToBottom}
            sx={{
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              '&:hover': {
                transform: 'scale(1.1)',
                boxShadow: '0 6px 16px rgba(0, 0, 0, 0.2)',
              },
              transition: 'all 0.3s ease',
            }}
            aria-label="scroll to bottom"
          >
            <KeyboardArrowDownIcon />
          </Fab>
        </Box>
      </Zoom>
    </React.Fragment>
  );
}

Dealers.propTypes = propTypes;
Dealers.defaultProps = defaultProps;
